import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useDocMetaHelper } from '@affine/core/components/hooks/use-block-suite-page-meta';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { FeatureName } from '@affine/graphql';
import { type DocMode, titleMiddleware } from '@blocksuite/affine/blocks';
import { Job, Slice } from '@blocksuite/affine/store';
import {
  DocsService,
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import { PagePropertiesManager } from '../../affine/doc-properties';
import { useDocHelper } from '../../blocksuite/block-suite-page-list/utils';
import { useCurrentWorkspacePropertiesAdapter } from '../use-affine-adapter';
import { useNavigateHelper } from '../use-navigate-helper';

export function useBlockSuiteMetaHelper() {
  const { workspaceService, docsService, workspaceQuotaService } = useServices({
    WorkspaceService,
    DocsService,
    WorkspaceQuotaService,
  });

  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
  }, [workspaceQuotaService]);

  const workspace = workspaceService.workspace;
  const docRecordList = docsService.list;

  const quota = useLiveData(workspaceQuotaService.quota.quota$);
  const max = useLiveData(workspaceQuotaService.quota.max$);

  const { setDocReadonly } = useDocMetaHelper();
  const { createDoc } = useDocHelper();
  const { openPage } = useNavigateHelper();

  const adapter = useCurrentWorkspacePropertiesAdapter();

  // "Remove" may cause ambiguity here. Consider renaming as "moveToTrash".
  const removeToTrash = useCallback(
    (docId: string) => {
      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        docRecord.moveToTrash();
        setDocReadonly(docId, true);
      }
    },
    [docRecordList, setDocReadonly]
  );

  const restoreFromTrash = useCallback(
    (docId: string) => {
      const used = workspaceQuotaService.quota.used$.value;

      if (
        used.usedDocCount + 1 > max.docLimit &&
        quota?.name !== FeatureName.unlimited
      ) {
        workspaceQuotaService.dialog.open();
        return;
      }

      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        docRecord.restoreFromTrash();
        setDocReadonly(docId, false);
      }

      return docRecord;
    },
    [
      docRecordList,
      max.docLimit,
      quota?.name,
      setDocReadonly,
      workspaceQuotaService.dialog,
      workspaceQuotaService.quota.used$.value,
    ]
  );

  const permanentlyDeletePage = useCallback(
    (pageId: string) => {
      workspace.docCollection.removeDoc(pageId);
    },
    [workspace]
  );

  const duplicate = useAsyncCallback(
    async (pageId: string, openPageAfterDuplication: boolean = true) => {
      const docRecord = createDoc();
      if (!docRecord) {
        return;
      }

      const { doc: newDoc, release: newDocRelease } = docsService.open(
        docRecord.id
      );
      const { doc: currentDoc, release: currentDocRelease } =
        docsService.open(pageId);

      await currentDoc.waitForSyncReady();

      try {
        const newDocBs = workspace.docCollection.getDoc(newDoc.id);
        const currentDocBs = workspace.docCollection.getDoc(currentDoc.id);

        if (!newDocBs) throw new Error('New doc BS doc not found');
        if (!currentDocBs) throw new Error('Current Doc BS doc not found');

        // очистить новый документ от всех блоков
        // например, от пустого блока paragraph
        newDocBs.root?.children.forEach(child => newDocBs.deleteBlock(child));

        const slice = Slice.fromModels(currentDocBs, [
          ...(currentDocBs.root?.children ?? []),
        ]);

        const job = new Job({
          collection: currentDocBs.collection,
          middlewares: [titleMiddleware],
          docCRUD: {
            create: (id: string) => {
              return workspace.docCollection.createDoc({ id });
            },
          },
        });

        const snapshot = await job.sliceToSnapshot(slice);
        if (!snapshot) {
          throw new Error('Failed to create snapshot');
        }

        await job.snapshotToSlice(snapshot, newDocBs, newDocBs.root?.id);
      } catch (error) {
        console.error('Failed to duplicate doc', {
          currentDoc,
          newDoc,
          error,
        });
      } finally {
        currentDocRelease();
        newDocRelease();
      }

      newDoc.record.setMeta({
        tags: currentDoc.meta$.value.tags,
      });

      const lastDigitRegex = /\((\d+)\)$/;
      const match = currentDoc.title$.value?.match(lastDigitRegex);
      const newNumber = match ? parseInt(match[1], 10) + 1 : 1;

      const newPageTitle =
        currentDoc.title$.value?.replace(lastDigitRegex, '') + `(${newNumber})`;

      const currentPagePrimaryMode =
        docRecordList.doc$(pageId).value?.primaryMode$.value;
      docRecordList
        .doc$(newDoc.id)
        .value?.setPrimaryMode(currentPagePrimaryMode || ('page' as DocMode));

      await docsService.changeDocTitle(newDoc.id, newPageTitle);

      const properties = currentDoc.getProperties();
      const removedProperties = ['id', 'isTemplate', 'journal'];
      removedProperties.forEach(key => {
        delete properties[key];
      });
      newDoc.updateProperties(properties);

      const propertiesCurrentDoc = new PagePropertiesManager(
        adapter,
        currentDoc.id
      );
      const propertiesNewDoc = new PagePropertiesManager(adapter, newDoc.id);

      const itemsCustomProperties = propertiesCurrentDoc.getItems();
      itemsCustomProperties.forEach(itemCustomProperty => {
        propertiesNewDoc.addCustomProperty(itemCustomProperty.id);
        propertiesNewDoc.updateCustomProperty(itemCustomProperty.id, {
          ...itemCustomProperty,
        });
      });

      openPageAfterDuplication &&
        openPage(workspace.docCollection.id, newDoc.id);
    },
    [
      createDoc,
      docsService,
      docRecordList,
      adapter,
      openPage,
      workspace.docCollection,
    ]
  );

  return {
    removeToTrash,
    restoreFromTrash,
    permanentlyDeletePage,

    duplicate,
  };
}
